
export const AWS_COGNITO_USER_POOL_CLIENT_ID = process.env.REACT_APP_AWS_COGNITO_USER_POOL_CLIENT_ID
export const AWS_COGNITO_USER_POOL_ID = process.env.REACT_APP_AWS_COGNITO_USER_POOL_ID

export const DOWNLOAD_PDF_URL = `${process.env.REACT_APP_ASSETS_URL}${process.env.REACT_APP_DATA_NEEDED_PDF}`
export const GAS_RECOVERY_PDF_URL = `${process.env.REACT_APP_ASSETS_URL}/pdfs/Landfills_with_and_without_gas_recovery.pdf`
export const COMPLETED_VIEWS_KEY = "completedViews"

export const APP_TITLE = "Orchard carbon footprint calculator"
export const KM_DRIVEN = "km driven in a medium car"
export const FLIGHTS_TAKEN = "one way flights to London in economy class"
export const C02_SEQUESTERED = "The annual CO2 sequestered by"
export const HECTARES_OF_PINE = "Hectares of growing pine"
export const HECTARES_OF_FOREST =
  "Hectares of permanently planted indigenous forest"
export const GRAPH_DISCLAIMER_TEXT =
  "If your values here are considerably different from the benchmarks, it may be that the incorrect data has been entered."
export const DISCLAIMER_VIEW_HEADING = "Disclaimer"
export const DISCLAIMER_VIEW_BUTTON = "I understand and accept"
export const DISCLAIMER_VIEW = {
    title: "Welcome",
    includeInNav: false,
    path: "/",
        description:
      "This tool has been developed to support you to measure and manage your carbon footprint i.e. greenhouse gas emissions. You will be able to see what your emissions are for each category (e.g. fuel, fertiliser) and what your hot spots are, and also how you compare to an industry average. You will also be able to change your inputs on a “What-if” page to see what impact this has on your overall emissions. This is the first version of the tool and we welcome any feedback which will help us to make it a better experience for you. Please use the feedback link at the bottom of any of the pages in the tool.",
    sections: [
      {
        key:'disclaimer',
        inputFields: [
          {
            field: "shareToZespri",
            section: "calculatorDetails",
            type: "radio",
            required: true,
            options: [
              {
                value: true,
                label: "I consent to Zespri collecting, using and storing my personal information in accordance with the Privacy Notice."
              }
            ],
          },
        ],
      },
    ],
      info: [
        {
        type: "list",
        value: [
          "Zespri Group Limited (“Zespri”) would like to collect your orchard information for the purposes of more accurately calculating and reporting industry emissions. Your individual data will be kept confidential. We will hold your personal information for 1 year or as long as necessary for the purposes for which it was collected. Your email address will be used to send you a secure link and your identifiers (orchard name, KPIN) will assist you to complete and review results for a particular orchard.",
          "We use a trusted third-party provider, GPSit, to collect and store your information securely. Your personal information will be managed in accordance with <a href='https://gpsit.co.nz/privacy_policy' target='_blank'>GPSit's Privacy Policy</a>.",
          "You have rights over the information Zespri holds about you. You may withdraw your consent for us to use your personal information at any time. You are entitled to ask for a copy of the personal information we hold about you, and to ask us to correct it if it's wrong.  For information about your privacy rights, please see our <a href='https://www.zespri.com/en-NZ/privacypolicy' target='_blank'>Privacy Statement</a>. To make a complaint or to exercise your privacy rights, contact our Global Privacy Team at <a href='mailto:privacy@zespri.com'>privacy@zespri.com</a>. You are also entitled to complain to the relevant data protection authority."
        ],
      },
    ],
    nextButtonText: "I understand and accept",
  }

export const VIEWS = [
  {
    title: "Introduction",
    path: "introduction",
    description: `This tool provides an estimate of greenhouse gas emissions (gross) for a kiwifruit orchard. It does not currently take into account carbon storage/sequestration, or carbon emissions or removals relating to land use change. You will need to provide the following to calculate your footprint: production data, amount of fuel used (or tractor hours), electricity used, amounts of nutrients (N,P,K,S,Mg, lime and dolomite), amount of compost applied, number of applications of each agchem, kgs of any inorganic waste, some information about your pergola structure or artificial shelter. Each of these has associated emissions from their manufacture and/or use. Please provide data for the whole season of interest, starting from after the previous harvest up to and including the harvest for the season concerned.<br /> <a href='${DOWNLOAD_PDF_URL}' target="_blank">For a detailed list of what information you need to enter, click here</a>.`,
    info: [
      {
        value: "Some helpful tips for using the tool.",
        type: "text",
      },
      {
        type: "list",
        value: [
          "Please read the text at the top of each page - this is important to ensure the right information is entered throughout.",
          "Use the back and next button on the bottom of the page to navigate between pages, or click page tab above e.g. Production",
          "This calculator saves your data after each step so you can come back and complete it at a later stage.",
        ],
      },
    ],
  },
  {
    title: "Orchard Overview",
    path: "orchard-overview",
    sections: [
      {
        title: "1. Orchard details",
        inputFields: [
          {
            label: "Orchard Name",
            field: "orchardName",
            section: "calculatorDetails",
            type: "text",
            required: true,
          },
          {
            label: "KPIN",
            field: "kpin",
            section: "calculatorDetails",
            type: "text",
            required: true,
            readOnly: "onSave",
            helpText:
              "Note - This can not be changed once the calculator has been saved",
          },
          {
            label: "Growing Method",
            field: "growingMethod",
            section: "calculatorDetails",
            type: "select",
            required: true,
            options: [
              {
                value: "Conventional",
                label: "Conventional",
              },
              {
                value: "Organic",
                label: "Organic",
              },
              {
                value: "Converting to organic",
                label: "Converting to organic",
              },
            ],
          },
          {
            label: "Growing Region",
            field: "growingRegion",
            section: "calculatorDetails",
            type: "select",
            required: true,
            options: [
              {
                value: "Bay of Plenty",
                label: "Bay of Plenty",
              },
              {
                value: "Hawkes Bay",
                label: "Hawkes Bay",
              },
              {
                value: "Poverty Bay",
                label: "Poverty Bay",
              },
              {
                value: "Waikato",
                label: "Waikato",
              },
              {
                value: "Auckland",
                label: "Auckland",
              },
              {
                value: "Nelson",
                label: "Nelson",
              },
              {
                value: "Northland",
                label: "Northland",
              },
              {
                value: "Sth North Island",
                label: "Sth North Island",
              },
            ],
          },
          {
            label: "Growing Season",
            field: "growingSeason",
            section: "calculatorDetails",
            type: "select",
            required: true,
            options: [
              {
                value: "2026/27",
                label: "2026/27",
              },
              {
                value: "2025/26",
                label: "2025/26",
              },
              {
                value: "2024/25",
                label: "2024/25",
              },
              {
                value: "2023/24",
                label: "2023/24",
              },
              {
                value: "2022/23",
                label: "2022/23",
              },
              {
                value: "2021/22",
                label: "2021/22",
              },
              {
                value: "2020/21",
                label: "2020/21",
              },
            ],
          },
        ],
      },
    ],
  },
  {
    title: "Production",
    path: "production",
    info: [
      {
        type: "text",
        value:
          "Only enter data below and throughout for kiwifruit only; exclude any other crops and any inputs associated with those.",
      },
    ],
    sections: [
      {
        title: "1. Area (producing hectares)",
        totalCalc: {
          field: "totalProductiveArea",
          section: "calculatorDetails",
          label: "Total kiwifruit area",
          unit: "ha",
        },
        inputFields: [
          {
            label: "Green canopy area",
            type: "number",
            min: 0,
            field: "productiveCanopyArea",
            subsection: "green",
            section: "varieties",
            unit: "ha",
            placeholder: 0,
          },
          {
            label: "Gold canopy area",
            type: "number",
            min: 0,
            field: "productiveCanopyArea",
            subsection: "gold",
            section: "varieties",
            unit: "ha",
            placeholder: 0,
          },
          {
            label: "Other varieties",
            type: "number",
            min: 0,
            field: "productiveCanopyArea",
            subsection: "other",
            section: "varieties",
            unit: "ha",
            placeholder: 0,
          },
        ],
      },
      {
        title: "2. Total Kgs leaving the orchard",
        info: "You can find this information on your packout report. Reject information is not required here as this is considered when measuring packhouse emissions.",
        totalCalc: {
          field: "totalKgLeavingOrchard",
          section: "calculatorDetails",
          label: "Total",
          unit: "kg",
        },
        inputFields: [
          {
            label: "Green",
            type: "number",
            min: 0,
            field: "totalKgLeavingOrchard",
            subsection: "green",
            section: "varieties",
            unit: "kg",
            placeholder: 0,
          },
          {
            label: "Gold",
            type: "number",
            min: 0,
            field: "totalKgLeavingOrchard",
            subsection: "gold",
            section: "varieties",
            unit: "kg",
            placeholder: 0,
          },
          {
            label: "Other varieties",
            type: "number",
            min: 0,
            field: "totalKgLeavingOrchard",
            subsection: "other",
            section: "varieties",
            unit: "kg",
            placeholder: 0,
          },
        ],
      },
      {
        title: "3. Total class 1 trays (gross)",
        info: "This is the gross amount that includes any subsequent fruit loss.",
        totalCalc: {
          field: "totalClass1Trays",
          section: "calculatorDetails",
          label: "Total",
        },
        inputFields: [
          {
            label: "Green",
            type: "number",
            min: 0,
            field: "totalClass1Trays",
            subsection: "green",
            section: "varieties",
            placeholder: 0,
          },
          {
            label: "Gold",
            type: "number",
            min: 0,
            field: "totalClass1Trays",
            subsection: "gold",
            section: "varieties",
            placeholder: 0,
          },
          {
            label: "Other varieties",
            type: "number",
            min: 0,
            field: "totalClass1Trays",
            subsection: "other",
            section: "varieties",
            placeholder: 0,
          },
        ],
      },
      {
        title: "4. Additional information",
        inputFields: [
          {
            label: "Production notes",
            type: "textArea",
            field: "production",
            subsection: "notes",
            section: "calculatorDetails",
          },
        ],
      },
    ],
  },
  {
    title: "Orchard Energy",
    path: "orchard-energy",
    info: [
      {
        type: "text",
        value:
          "Enter total values for your fuel used across the whole orchard. Exclude contractor fuel as this is captured on the next page, and exclude any fuel not used on this orchard. If your energy use was zero, please enter zero values below where required.",
      },
    ],
    sections: [
      {
        title: "1. Diesel usage",
        inputFields: [
          {
            legend:
              "Do you know the total diesel use on your orchard this season, or the total running hours spent on orchard activities such as spraying and mowing?",
            type: "radio",
            field: "knowFuelUse",
            section: "calculatorDetails",
            required: true,
            options: [
              {
                value: true,
                label: "I know my total diesel use",
                subFields: [
                  {
                    label:
                      "Total amount of diesel fuel used on this orchard this season",
                    type: "number",
                    min: 0,
                    unit: "Litres",
                    field: "dieselUsed",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                ],
              },
              {
                value: false,
                label: "I know the total hours running spent on each activity",
                subFields: [
                  {
                    label: "Total running time for Spraying",
                    type: "number",
                    min: 0,
                    unit: "hours",
                    field: "selfSprayingHours",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Total running time for Mowing",
                    type: "number",
                    min: 0,
                    unit: "hours",
                    field: "selfMowingHours",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Total running time for Mulching",
                    type: "number",
                    min: 0,
                    unit: "hours",
                    field: "selfMulchingHours",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label:
                      "Total running time for other activities - such as applying fertiliser",
                    type: "number",
                    min: 0,
                    unit: "hours",
                    field: "selfOtherHours",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label:
                      "Total running time for harvest (i.e using your own tractor)",
                    type: "number",
                    min: 0,
                    unit: "hours",
                    field: "selfHarvestHours",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        title: "2. Other fuels",
        inputFields: [
          {
            label:
              "Total amount of Petrol fuel used on this orchard this season",
            type: "number",
            min: 0,
            field: "petrolUsed",
            section: "calculatorDetails",
            unit: "Litres",
            placeholder: 0,
          },
          {
            label: "Total amount of Lubricant used on this orchard this season",
            type: "number",
            min: 0,
            field: "lubricantUsed",
            section: "calculatorDetails",
            unit: "Litres",
            placeholder: 0,
          },
        ],
      },
      {
        title: "3. Electricity use",
        inputFields: [
          {
            label:
              "Total amount of electricity used in KWh for this orchard this season (exclude any domestic/house electricity)",
            type: "number",
            min: 0,
            field: "electricityUsed",
            section: "calculatorDetails",
            unit: "kWh",
            placeholder: 0,
          },
        ],
      },
      {
        title: "4. Additional information",
        inputFields: [
          {
            label: "Orchard energy notes",
            type: "textArea",
            field: "orchardEnergy",
            subsection: "notes",
            section: "calculatorDetails",
          },
        ],
      },
    ],
  },
  {
    title: "Contractor Fuel Use",
    path: "contractor-fuel",
    info: [
      {
        type: "text",
        value:
          "Enter the total values for the whole orchard and season below. This is fuel used by contactors and excludes your own fuel use. This is difficult to quantify so do your best to estimate how many hours is spent on each activity during the growing season across all varieties.",
      },
    ],
    sections: [
      {
        title: "1. Contracted activity (not including your own)",
        inputFields: [
          {
            label: "Total running time for Spraying",
            type: "number",
            min: 0,
            unit: "hours",
            field: "contractorSprayingHours",
            section: "calculatorDetails",
            placeholder: 0,
          },
          {
            label: "Total running time for Shelter belt trimming",
            type: "number",
            min: 0,
            unit: "hours",
            field: "contractorShelterBeltTrimmingHours",
            section: "calculatorDetails",
            placeholder: 0,
          },
          {
            label: "Total running time for Mulching",
            type: "number",
            min: 0,
            unit: "hours",
            field: "contractorMulchingHours",
            section: "calculatorDetails",
            placeholder: 0,
          },
          {
            label: "Total running time for Mowing",
            type: "number",
            min: 0,
            unit: "hours",
            field: "contractorMowingHours",
            section: "calculatorDetails",
            placeholder: 0,
          },
          {
            label: "Total running time for harvest (i.e using a tractor)",
            type: "number",
            min: 0,
            unit: "hours",
            field: "contractorHarvestHours",
            section: "calculatorDetails",
            placeholder: 0,
          },
          {
            label: "Total running time for Helicopters",
            type: "number",
            min: 0,
            unit: "hours",
            field: "contractorHelicopterHours",
            section: "calculatorDetails",
            placeholder: 0,
          },
          {
            label:
              "Total running time for other activities - such as applying fertiliser",
            type: "number",
            min: 0,
            unit: "hours",
            field: "contractorOtherHours",
            section: "calculatorDetails",
            placeholder: 0,
          },
        ],
      },
      {
        title: "2. Additional information",
        inputFields: [
          {
            label: "Contractor fuel use notes",
            type: "textArea",
            field: "contractorFuelUse",
            subsection: "notes",
            section: "calculatorDetails",
          },
        ],
      },
    ],
  },
  {
    title: "Fertilisers",
    path: "fertilisers",
    info: [
      {
        type: "text",
        value:
          "The manufacture of fertilisers results in emissions. Also, when some fertilisers are applied to the soil, greenhouse gases like nitrous oxide are released. Enter per hectare values for each variety. The total amount of each nutrient applied per hectare (not product) is required.",
      },
    ],
    sections: [
      {
        title: "1. Soil applied inorganic nutrients (including fertigation)",
        inputFields: [
          {
            label: "Nutrients applied total kg/ha",
            type: "table",
            section: "varieties",
            columns: [
              {
                subsection: "green",
                label: "Green",
              },
              {
                subsection: "gold",
                label: "Gold",
              },
              {
                subsection: "other",
                label: "Other",
              },
            ],
            rows: [
              {
                label: "Nitrogen",
                type: "number",
                min: 0,
                max: 250,
                field: "nitrogenApplied",
                placeholder: 0,
              },
              {
                label: "Phosphorous",
                type: "number",
                min: 0,
                max: 150,
                field: "phosphorusApplied",
                placeholder: 0,
              },
              {
                label: "Potassium",
                type: "number",
                min: 0,
                max: 550,
                field: "potassiumApplied",
                placeholder: 0,
              },
              {
                label: "Sulphur",
                type: "number",
                min: 0,
                max: 500,
                field: "sulphurApplied",
                placeholder: 0,
              },
              {
                label: "Magnesium",
                type: "number",
                min: 0,
                max: 150,
                field: "magnesiumApplied",
                placeholder: 0,
              },
              {
                label: "Lime",
                type: "number",
                min: 0,
                max: 5000,
                field: "limeApplied",
                placeholder: 0,
              },
              {
                label: "Dolomite",
                type: "number",
                min: 0,
                max: 5000,
                field: "dolomiteApplied",
                placeholder: 0,
              },
            ],
          },
          {
            label: "Main soil applied non-urea nitrogen product",
            type: "select",
            required: true,
            options: [
              {
                value: "DAP",
                label: "DAP",
              },
              {
                value: "SOA",
                label: "SOA",
              },
              {
                value: "CAN",
                label: "CAN",
              },
              {
                value: "OTHER",
                label: "Other",
              },
            ],
            field: "mainSoilAppliedNonUreaNitrogenProduct",
            section: "calculatorDetails",
            helpText:
              "Note - If your main soil applied nitrogen is urea, make sure the total amount of N is entered above and below in the urea section, otherwise your nitrogen related emissions will not be calculated correctly",
          },
        ],
      },
      {
        title: "2. Foliar nitrogen - excluding any budbreakers (e.g: HiCane)",
        subTitle: "Nitrogen applied total kg/ha",
        inputFields: [
          {
            type: "table",
            section: "varieties",
            columns: [
              {
                subsection: "green",
                label: "Green",
              },
              {
                subsection: "gold",
                label: "Gold",
              },
              {
                subsection: "other",
                label: "Other",
              },
            ],
            rows: [
              {
                label: "Foliar Nitrogen",
                type: "number",
                min: 0,
                max: 50,
                placeholder: 0,
                field: "foliarNitrogenApplied",
              },
            ],
          },
        ],
      },
      {
        title: "3. Soil applied urea (optional)",
        subTitle: "Nitrogen applied total kg/ha",
        inputFields: [
          {
            type: "table",
            section: "varieties",
            description:
              "Enter kg N/ha values for soil applied Urea vs non-Urea. Also enter total nitrogen values above which should equal values below.",
            columns: [
              {
                subsection: "green",
                label: "Green",
              },
              {
                subsection: "gold",
                label: "Gold",
              },
              {
                subsection: "other",
                label: "Other",
              },
            ],
            rows: [
              {
                label: "Non-urea N",
                type: "number",
                min: 0,
                max: 250,
                field: "nonUreaNitrogenApplied",
                placeholder: 0,
              },
              {
                label: "Urea N w/Urease Inhibitor",
                type: "number",
                min: 0,
                max: 250,
                field: "ureaNitrogenAppliedWithInhibitor",
                placeholder: 0,
              },
              {
                label: "Urea N (no Urease Inhibitor)",
                placeholder: 0,
                type: "number",
                min: 0,
                max: 250,
                field: "ureaNitrogenAppliedNoInhibitor",
                placeholder: 0,
              },
            ],
          },
        ],
      },
      {
        title: "4. Additional information",
        inputFields: [
          {
            label: "Fertiliser notes",
            type: "textArea",
            field: "fertiliser",
            subsection: "notes",
            section: "calculatorDetails",
          },
        ],
      },
    ],
  },
  {
    title: "Compost",
    path: "compost",
    info: [
      {
        type: "text",
        value:
          "Enter per hectare values for each variety on this page. If you only apply compost to parts of your orchard, estimate what the average per hectare values would be if your current total amount was spread across the whole orchard.",
      },
    ],
    sections: [
      {
        title: "1. Standard compost (Greenwaste)",
        inputFields: [
          {
            label: "Greenwaste compost in tonnes/ha",
            type: "table",
            section: "varieties",
            columns: [
              {
                subsection: "green",
                label: "Green",
              },
              {
                subsection: "gold",
                label: "Gold",
              },
              {
                subsection: "other",
                label: "Other",
              },
            ],
            rows: [
              {
                label: "Quantity",
                type: "number",
                min: 0,
                max: 50,
                field: "greenWasteCompost",
                unit: "t/ha",
                placeholder: 0,
              },
            ],
          },
          {
            legend:
              "Do you know your nitrogen and dry matter percentages for your Greenwaste compost?",
            type: "radio",
            field: "knowGreenWasteCompostDetails",
            section: "calculatorDetails",
            required: false,
            initialValue: false,
            options: [
              {
                value: false,
                label:
                  "Use average nitrogen (1.5%) and dry matter (53%) values",
                fieldsToSet: [
                  {
                    field: "compostNitrogenPercent",
                    value: 1.5,
                  },
                  {
                    field: "compostDryMatter",
                    value: 53,
                  },
                ],
              },
              {
                value: true,
                label: "I know my nitrogen and dry matter percentages",
                subFields: [
                  {
                    label: "Nitrogen % in greenwaste compost",
                    type: "number",
                    min: 0,
                    max: 100,
                    unit: "%",
                    field: "compostNitrogenPercent",
                    section: "calculatorDetails",
                    initialValue: 1.5,
                  },
                  {
                    label: "Dry matter % in greenwaste compost",
                    type: "number",
                    min: 0,
                    max: 100,
                    unit: "%",
                    field: "compostDryMatter",
                    section: "calculatorDetails",
                    initialValue: 53,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        title: "2. Chicken manure/compost",
        inputFields: [
          {
            label: "Chicken manure compost in tonnes/ha",
            type: "table",
            section: "varieties",
            columns: [
              {
                subsection: "green",
                label: "Green",
              },
              {
                subsection: "gold",
                label: "Gold",
              },
              {
                subsection: "other",
                label: "Other",
              },
            ],
            rows: [
              {
                label: "Quantity",
                type: "number",
                min: 0,
                max: 50,
                field: "chickenManureCompost",
                unit: "t/ha",
                placeholder: 0,
              },
            ],
          },
          {
            legend:
              "Do you know your nitrogen and dry matter percentages for your Chicken Manure?",
            type: "radio",
            field: "knowManureDetails",
            section: "calculatorDetails",
            required: false,

            initialValue: false,
            options: [
              {
                value: false,
                label:
                  "Use average nitrogen (2.1%) and dry matter (57%) values",
                fieldsToSet: [
                  {
                    field: "manureNitrogenPercent",
                    value: 2.1,
                  },
                  {
                    field: "manureDryMatter",
                    value: 57,
                  },
                ],
              },
              {
                value: true,
                label: "I know my nitrogen and dry matter percentages",
                subFields: [
                  {
                    label: "Nitrogen % in chicken manure",
                    type: "number",
                    min: 0,
                    max: 100,
                    unit: "%",
                    field: "manureNitrogenPercent",
                    section: "calculatorDetails",
                    initialValue: 2.1,
                  },
                  {
                    label: "Dry matter % in chicken manure",
                    type: "number",
                    min: 0,
                    max: 100,
                    unit: "%",
                    field: "manureDryMatter",
                    section: "calculatorDetails",
                    initialValue: 57,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        title: "3. Additional information",
        inputFields: [
          {
            label: "Compost notes",
            type: "textArea",
            field: "compost",
            subsection: "notes",
            section: "calculatorDetails",
          },
        ],
      },
    ],
  },
  {
    title: "Agrichemicals",
    path: "agrichemicals",
    info: [
      {
        type: "text",
        value:
          "The manufacture of agrichemicals results in emissions. Only the main products according to the total quantities used across the industry are listed below. Some common products may be excluded because so little is applied per hectare and is therefore immaterial to the overall orchard footprint e.g. those containing Emamectin e.g: Announce and Proclaim. To keep the tool as simple as possible, we have done some previous analysis that indicates the number of applications provides a good estimate of the associated emissions. You may input the number of applications of other products used if they share the same active ingredient as any of those listed.",
      },
    ],
    sections: [
      {
        title: "1. Agrichemical applications",
        inputFields: [
          {
            label: "Number of applications",
            type: "table",
            section: "varieties",
            columns: [
              {
                subsection: "green",
                label: "Green",
              },
              {
                subsection: "gold",
                label: "Gold",
              },
              {
                subsection: "other",
                label: "Other",
              },
            ],
            rows: [
              {
                label: "Hi-Cane or similar",
                field: "hiCaneApplications",
                type: "number",
                min: 0,
                max: 2,
                placeholder: 0,
              },
              {
                label: "Kocide opti or similar",
                field: "kocideOptiApplications",
                type: "number",
                min: 0,
                max: 10,
                placeholder: 0,
              },
              {
                label: "Nordox 75 WG or similar",
                field: "nordox75WGApplications",
                type: "number",
                min: 0,
                max: 10,
                placeholder: 0,
              },
              {
                label: "Copper sulphate",
                field: "copperSulphateApplications",
                type: "number",
                min: 0,
                max: 3,
                placeholder: 0,
              },
              {
                label: "D-C Tron Plus/Excel oil/Enspray 99",
                field: "dcTronApplications",
                type: "number",
                min: 0,
                max: 5,
                placeholder: 0,
              },
              {
                label: "Prodigy / Talent",
                field: "prodigyApplications",
                type: "number",
                min: 0,
                max: 5,
                placeholder: 0,
              },
              {
                label: "Movento 100 SC",
                field: "moventoApplications",
                type: "number",
                min: 0,
                max: 5,
                placeholder: 0,
              },
              {
                label: "Roundup",
                field: "roundUpApplications",
                type: "number",
                min: 0,
                max: 5,
                placeholder: 0,
              },
              {
                label: "Flock Off",
                field: "flockOffApplications",
                type: "number",
                min: 0,
                max: 5,
                placeholder: 0,
              },
              {
                label: "Kasumin",
                field: "kasuminApplications",
                type: "number",
                min: 0,
                max: 3,
                placeholder: 0,
              },
              {
                label: "Du-wett",
                field: "duWettApplications",
                type: "number",
                min: 0,
                max: 15,
                placeholder: 0,
              },
              {
                label: "Driftstop",
                field: "driftStopApplications",
                type: "number",
                min: 0,
                max: 5,
                placeholder: 0,
              },
              {
                label: "Triple X",
                field: "tripleXApplications",
                type: "number",
                min: 0,
                max: 7,
                placeholder: 0,
              },
              {
                label: "Biobit DF / Delfin WG / Dipel DF",
                field: "biobitApplications",
                type: "number",
                min: 0,
                max: 4,
                placeholder: 0,
              },
              {
                label: "Pyrethrum eg Pyganic, Zetapy, Pylon",
                field: "pyrethrumApplications",
                type: "number",
                min: 0,
                max: 10,
                placeholder: 0,
              },
              {
                label: "Phosphoric acid (fruit cleanser)",
                field: "phosphoricAcidApplications",
                type: "number",
                min: 0,
                max: 3,
                placeholder: 0,
              },
              {
                label: "Lactic acid (fruit cleanser)",
                field: "lacticAcidApplications",
                type: "number",
                min: 0,
                max: 3,
                placeholder: 0,
              },
              {
                label: "Actigard",
                field: "actigardApplications",
                type: "number",
                min: 0,
                max: 4,
                placeholder: 0,
              },
              {
                label: "Assail / Venom / Telemark",
                field: "assailApplications",
                type: "number",
                min: 0,
                max: 3,
                placeholder: 0,
              },
              {
                label: "Luna Privilege",
                field: "lunaApplications",
                type: "number",
                min: 0,
                max: 3,
                placeholder: 0,
              },
            ],
          },
        ],
      },
      {
        title: "2. Additional information",
        inputFields: [
          {
            label: "Agrichemical notes",
            type: "textArea",
            field: "agrichemicals",
            subsection: "notes",
            section: "calculatorDetails",
          },
        ],
      },
    ],
  },
  {
    title: "Netting",
    path: "netting",
    info: [
      {
        type: "text",
        value:
          "Enter the total values for your orchard. Capital emissions result during the manufacture of the capital. The calculated emissions are the annual average values based on the expected lifespan.",
      },
    ],
    sections: [
      {
        title: "1. Hail / overhead shelter",
        inputFields: [
          {
            label: "Overhead shelter",
            field: "hasOverHead",
            section: "calculatorDetails",
            type: "radio",
            required: true,
            options: [
              {
                value: false,
                label: "I don't have any overhead shelters",
                fieldsToSet: [
                  {
                    field: "overheadTotalLengthOfNetting",
                    value: 0,
                  },
                  {
                    field: "overheadTotalWidthOfNetting",
                    value: 0,
                  },
                  {
                    field: "overheadTotalNumOfPosts",
                    value: 0,
                  },
                  {
                    field: "overheadLengthPerPost",
                    value: 0,
                  },
                  {
                    field: "overheadDiameterPerPost",
                    value: 0,
                  },
                  {
                    field: "overheadTotalSteelCableLength",
                    value: 0,
                  },
                ],
              },
              {
                value: true,
                label: "I have overhead shelters",
                subFields: [
                  {
                    label: "Total length of netting",
                    type: "number",
                    min: 0,
                    field: "overheadTotalLengthOfNetting",
                    unit: "m",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Total width of netting",
                    type: "number",
                    min: 0,
                    field: "overheadTotalWidthOfNetting",
                    unit: "m",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Total number of supporting posts",
                    type: "number",
                    min: 0,
                    field: "overheadTotalNumOfPosts",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Length per post",
                    type: "number",
                    min: 3,
                    max: 10,
                    field: "overheadLengthPerPost",
                    unit: "m",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Diameter per post",
                    type: "number",
                    min: 50,
                    max: 500,
                    field: "overheadDiameterPerPost",
                    unit: "mm",
                    section: "calculatorDetails",
                    placeholder: 50,
                  },
                  {
                    label: "Total length of steel cables",
                    type: "number",
                    min: 0,
                    field: "overheadTotalSteelCableLength",
                    unit: "m",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        title: "2. Artificial / vertical shelter",
        inputFields: [
          {
            label: "Artificial shelter",
            field: "hasArtificial",
            section: "calculatorDetails",
            type: "radio",
            required: true,
            options: [
              {
                value: false,
                label: "I don't have any artificial shelters",
                fieldsToSet: [
                  {
                    field: "overheadTotalLengthOfNetting",
                    value: 0,
                  },
                  {
                    field: "overheadTotalWidthOfNetting",
                    value: 0,
                  },
                  {
                    field: "overheadTotalNumOfPosts",
                    value: 0,
                  },
                  {
                    field: "overheadLengthPerPost",
                    value: 0,
                  },
                  {
                    field: "overheadDiameterPerPost",
                    value: 0,
                  },
                  {
                    field: "overheadTotalSteelCableLength",
                    value: 0,
                  },
                ],
              },
              {
                value: true,
                label: "I have artificial shelters",
                subFields: [
                  {
                    label: "Total length of netting",
                    type: "number",
                    min: 0,
                    field: "artificialTotalLengthOfNetting",
                    unit: "m",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Average height of netting",
                    type: "number",
                    min: 0,
                    field: "artificialTotalWidthOfNetting",
                    unit: "m",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Total number of supporting posts",
                    type: "number",
                    min: 0,
                    field: "artificialTotalNumberOfPost",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Length per post",
                    type: "number",
                    min: 1,
                    max: 10,
                    field: "artificialLengthPerPost",
                    unit: "m",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Diameter per post",
                    type: "number",
                    min: 50,
                    max: 500,
                    field: "artificialDiameterPerPost",
                    unit: "mm",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Total length of steel cables",
                    type: "number",
                    min: 0,
                    field: "artificialTotalSteelCableLength",
                    unit: "m",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        title: "3. Undervine shelter / Fast Track",
        inputFields: [
          {
            label: "Undervine shelter",
            field: "hasUndervine",
            section: "calculatorDetails",
            type: "radio",
            required: true,
            options: [
              {
                value: false,
                label: "I don't have any undervine shelters",
                fieldsToSet: [
                  {
                    field: "undervineTotalLengthOfNetting",
                    value: 0,
                  },
                  {
                    field: "undervineTotalWidthOfNetting",
                    value: 0,
                  },
                  {
                    field: "undervineWiresTotalLength",
                    value: 0,
                  },
                ],
              },
              {
                value: true,
                label: "I have undervine shelters",
                subFields: [
                  {
                    label: "Total length of netting",
                    type: "number",
                    min: 0,
                    field: "undervineTotalLengthOfNetting",
                    unit: "m",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Average height of netting",
                    type: "number",
                    min: 0,
                    field: "undervineTotalWidthOfNetting",
                    unit: "m",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                  {
                    label: "Wires - total length",
                    type: "number",
                    min: 0,
                    field: "undervineWiresTotalLength",
                    unit: "m",
                    section: "calculatorDetails",
                    placeholder: 0,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        title: "4. Additional information",
        inputFields: [
          {
            label: "Netting notes",
            type: "textArea",
            field: "netting",
            subsection: "notes",
            section: "calculatorDetails",
          },
        ],
      },
    ],
  },
  {
    title: "Pergola",
    path: "pergola",
    info: [
      {
        type: "text",
        value:
          "Enter the total values for the <strong>producing parts</strong> of your orchard. Orchard capital emissions results from the emissions embodied from capital manufacture. The calculated emissions are the annual values based on the expected lifespan. Capital emissions exclude emissions associated with machinery, implements, and buildings in accordance with the footprinting standards. The size of bays and structures may vary across the orchard so please use average values for the whole orchard.",
      },
    ],
    sections: [
      {
        title: "1. Main vertical posts - wooden",
        inputFields: [
          {
            label: "Length per post",
            type: "number",
            min: 1,
            max: 3,
            field: "woodMainVerticalPostLength",
            unit: "m",
            section: "calculatorDetails",
            required: true,
          },
          {
            label: "Diameter per post",
            type: "number",
            min: 50,
            max: 200,
            field: "woodMainVerticalPostDiameter",
            unit: "mm",
            section: "calculatorDetails",
            required: true,
          },
          {
            label: "% of orchard",
            type: "number",
            min: 0,
            field: "percentageOfOrchardIsWood",
            section: "calculatorDetails",
            max: 100,
            required: true,
          },
        ],
      },
      {
        title: "2. Main vertical posts - steel",
        inputFields: [
          {
            label: "Kilograms per post",
            type: "number",
            min: 0,
            field: "steelKgsPerPost",
            unit: "kg",
            section: "calculatorDetails",
          },
          {
            label: "% of orchard",
            type: "number",
            min: 0,
            field: "percentageOfOrchardIsSteel",
            section: "calculatorDetails",
            min: 0,
            max: 100,
          },
        ],
      },
      {
        title: "3. Horizontal beams / cables",
        inputFields: [
          {
            label: "Main type of material",
            type: "select",
            field: "horizontalBeamType",
            section: "calculatorDetails",
            required: true,
            options: [
              {
                value: "agBeam",
                label: "AgBeam",
              },
              {
                value: "steelCable",
                label: "Steel cable",
              },
              {
                value: "wood",
                label: "Wood",
              },
            ],
          },
          {
            label: "Bay width",
            type: "number",
            min: 2,
            max: 6,
            field: "bayWidth",
            required: true,
            unit: "m",
            section: "calculatorDetails",
          },
        ],
      },
      {
        title: "4. Wires running down bay",
        inputFields: [
          {
            label: "Number per bay",
            type: "number",
            min: 5,
            max: 12,
            field: "wiresRunningDownBayNumber",
            section: "calculatorDetails",
            required: true,
          },
          {
            label: "Bay Length",
            type: "number",
            min: 2,
            max: 6,
            field: "bayLength",
            unit: "m",
            section: "calculatorDetails",
            required: true,
          },
        ],
      },
      {
        title: "5. Additional information",
        inputFields: [
          {
            label: "Pergola notes",
            type: "textArea",
            field: "pergola",
            subsection: "notes",
            section: "calculatorDetails",
          },
        ],
      },
    ],
  },
  {
    title: "Waste",
    path: "waste-recycling",
    info: [
      {
        type: "text",
        value:
          "Please enter total values for the whole orchard. Only the following categories are available due to the lack of emissions factors at time of preparing this tool.",
      },
    ],
    sections: [
      {
        title: "1. Waste to landfill",
        inputFields: [
          {
            label: "Mixed waste (unknown composition)",
            type: "number",
            min: 0,
            field: "mixedWasteToLandfill",
            unit: "kg",
            section: "calculatorDetails",
            required: true,
          },
          {
            label: "Wood",
            type: "number",
            min: 0,
            field: "woodWasteToLandfill",
            unit: "kg",
            section: "calculatorDetails",
            required: true,
          },
          {
            label: `Does the landfill have gas recovery? <a href='${GAS_RECOVERY_PDF_URL}' target='_blank'>List of landfills with gas recovery</a>`,
            type: "select",
            field: "landfillHasGasRecovery",
            section: "calculatorDetails",
            options: [
              { value: true, label: "Gas recovery" },
              { value: false, label: "No gas recovery" },
            ],
            required: true,
          },
        ],
      },
      {
        title: "3. Additional information",
        inputFields: [
          {
            label: "Waste notes",
            type: "textArea",
            field: "wasteAndRecycling",
            subsection: "notes",
            section: "calculatorDetails",
          },
        ],
      },
    ],
  },
];

export const NAV_LINKS = [
  {
    path: "summary",
    title: "Orchard List",
  },
  ...VIEWS,
  {
    path: "results",
    title: "Results",
  },
  {
    path: "whatIf",
    title: "What if?",
  }
]

export const SELECT_PLACEHOLDER_VALUE = "Please select a value"
export const PREV_PAGE_TEXT = "Previous page"
export const NEXT_PAGE_TEXT = "Next page"
export const RESULTS_TEXT = "Results"
export const WHAT_IF_TEXT = "What if?";
export const WHAT_IF_TITLE = "Gross emissions"
export const WHAT_IF_SCENARIO_TEXT = "Scenario testing"

export const FINAL_VALUES = {
  calculatorDetails: {
    shareToZespri: true,
    shareName: "Zespri",
    email: "",
    orchardName: "",
    kpin: "",
    growingSeason: "",
    totalProductiveArea: 0,
    totalArea: 0,
    mainSoilAppliedNonUreaNitrogenProduct: "DAP",
    overheadTotalLengthOfNetting: 0,
    overheadTotalWidthOfNetting: 0,
    overheadTotalNumOfPosts: 0,
    overheadLengthPerPost: 0,
    overheadDiameterPerPost: 0,
    overheadTotalSteelCableLength: 0,
    artificialTotalLengthOfNetting: 0,
    artificialTotalWidthOfNetting: 0,
    artificialTotalNumberOfPost: 0,
    artificialLengthPerPost: 0,
    artificialDiameterPerPost: 0,
    artificialTotalSteelCableLength: 0,
    undervineTotalLengthOfNetting: 0,
    undervineTotalWidthOfNetting: 0,
    undervineWiresTotalLength: 0,
    woodMainVerticalPostLength: 0,
    woodMainVerticalPostDiameter: 0,
    steelKgsPerPost: 0,
    percentageOfOrchardIsWood: 100,
    percentageOfOrchardIsSteel: 0,
    deadmanPostLength: 0,
    deadmanPostDiameter: 0,
    deadmanPostTotalNumber: 0,
    strainerPostLength: 0,
    strainerPostDiameter: 0,
    strainerPostTotalNumber: 0,
    otherPostLength: 0,
    otherPostDiameter: 0,
    otherPostTotalNumber: 0,
    horizontalBeamType: "agBeam",
    wiresRunningDownBayNumber: 0,
    wiresRunningAcrossBayNumber: 0,
    bayWidth: 0,
    bayLength: 0,
    mixedWasteToLandfill: 0,
    woodWasteToLandfill: 0,
    landfillHasGasRecovery: false,
    mixedPlasticRecycling: 0,
    dieselUsed: 0,
    dieselUnits: "L",
    petrolUsed: 0,
    petrolUnits: "L",
    lubricantUsed: 0,
    electricityUsed: 0,
    electricityUnits: "kWh",
    selfSprayingHours: 0,
    selfMowingHours: 0,
    selfMulchingHours: 0,
    selfOtherHours: 0,
    selfHarvestHours: 0,
    contractorSprayingHours: 0,
    contractorShelterBeltTrimmingHours: 0,
    contractorMowingHours: 0,
    contractorMulchingHours: 0,
    contractorHarvestHours: 0,
    contractorHelicopterHours: 0,
    contractorOtherHours: 0,
    knowFuelUse: false,
    hasOverHead: false,
    hasArtificial: false,
    hasUndervine: false,
    knowGreenWasteCompostDetails: false,
    knowManureDetails: false,
    compostNitrogenPercent: 1.5, 
    compostDryMatter: 53, 
    manureNitrogenPercent:2.1,
    manureDryMatter:53,
    notes: {
      production: "",
      orchardEnergy: "",
      contractorFuelUse: "",
      fertiliser: "",
      compost: "",
      agrichemicals: "",
      netting: "",
      pergola: "",
      wasteAndRecycling: "",
      results: "",
      other: ""
    }
  },
  varieties: {
    green: {
      productiveCanopyArea: 0,
      totalKgLeavingOrchard: 0,
      totalClass1Trays: 0,
      nitrogenApplied: 0,
      phosphorusApplied: 0,
      potassiumApplied: 0,
      sulphurApplied: 0,
      magnesiumApplied: 0,
      limeApplied: 0,
      dolomiteApplied: 0,
      foliarNitrogenApplied: 0,
      nonUreaNitrogenApplied: 0,
      ureaNitrogenAppliedWithInhibitor: 0,
      ureaNitrogenAppliedNoInhibitor: 0,
      greenWasteCompost: 0,
      chickenManureCompost: 0,
      hiCaneApplications: 0,
      kocideOptiApplications: 0,
      nordox75WGApplications: 0,
      copperSulphateApplications: 0,
      dcTronApplications: 0,
      prodigyApplications: 0,
      moventoApplications: 0,
      roundUpApplications: 0,
      flockOffApplications: 0,
      kasuminApplications: 0,
      duWettApplications: 0,
      driftStopApplications: 0,
      tripleXApplications: 0,
      biobitApplications: 0,
      pyrethrumApplications: 0,
      phosphoricAcidApplications: 0,
      lacticAcidApplications: 0,
      actigardApplications: 0,
      assailApplications: 0,
      lunaApplications: 0,
      totalKgLeavingOrchard:0
    },
    gold: {
      productiveCanopyArea: 0,
      totalKgLeavingOrchard: 0,
      totalClass1Trays: 0,
      nitrogenApplied: 0,
      phosphorusApplied: 0,
      potassiumApplied: 0,
      sulphurApplied: 0,
      magnesiumApplied: 0,
      limeApplied: 0,
      dolomiteApplied: 0,
      foliarNitrogenApplied: 0,
      nonUreaNitrogenApplied: 0,
      ureaNitrogenAppliedWithInhibitor: 0,
      ureaNitrogenAppliedNoInhibitor: 0,
      greenWasteCompost: 0,
      chickenManureCompost: 0,
      hiCaneApplications: 0,
      kocideOptiApplications: 0,
      nordox75WGApplications: 0,
      copperSulphateApplications: 0,
      dcTronApplications: 0,
      prodigyApplications: 0,
      moventoApplications: 0,
      roundUpApplications: 0,
      flockOffApplications: 0,
      kasuminApplications: 0,
      duWettApplications: 0,
      driftStopApplications: 0,
      tripleXApplications: 0,
      biobitApplications: 0,
      pyrethrumApplications: 0,
      phosphoricAcidApplications: 0,
      lacticAcidApplications: 0,
      actigardApplications: 0,
      assailApplications: 0,
      lunaApplications: 0,
      totalKgLeavingOrchard:0
    },
    other: {
      productiveCanopyArea: 0,
      totalKgLeavingOrchard: 0,
      totalClass1Trays: 0,
      nitrogenApplied: 0,
      phosphorusApplied: 0,
      potassiumApplied: 0,
      sulphurApplied: 0,
      magnesiumApplied: 0,
      limeApplied: 0,
      dolomiteApplied: 0,
      foliarNitrogenApplied: 0,
      nonUreaNitrogenApplied: 0,
      ureaNitrogenAppliedWithInhibitor: 0,
      ureaNitrogenAppliedNoInhibitor: 0,
      greenWasteCompost: 0,
      chickenManureCompost: 0,
      hiCaneApplications: 0,
      kocideOptiApplications: 0,
      nordox75WGApplications: 0,
      copperSulphateApplications: 0,
      dcTronApplications: 0,
      prodigyApplications: 0,
      moventoApplications: 0,
      roundUpApplications: 0,
      flockOffApplications: 0,
      kasuminApplications: 0,
      duWettApplications: 0,
      driftStopApplications: 0,
      tripleXApplications: 0,
      biobitApplications: 0,
      pyrethrumApplications: 0,
      phosphoricAcidApplications: 0,
      lacticAcidApplications: 0,
      actigardApplications: 0,
      assailApplications: 0,
      lunaApplications: 0,
      totalKgLeavingOrchard:0
    },
  },
}

export const ERROR_MODAL_TITLE_TEXT = "Error"
export const LOADING_TEXT = "Updating"
export const RESULTS_KEY = "results"
export const CALCULATED_RESULTS_KEY = "calculatedResults"
export const WHAT_IF_RESULTS = "whatIfResults"
export const VIEW_RESULTS_LABEL_TEXT = "View result as"
export const VIEW_RESULTS_OPTIONS = [
  {
    value: "perHa",
    label: "Per hectare",
  },
  {
    value: "perKg",
    label: "Per kg of fruit",
  },
  {
    value: "perTray",
    label: "Per class 1 tray",
  },
]

export const EMISSION_CATEGORIES_HEADING_TEXT = "Emission Categories"
export const NITROGEN_FERTILISER_HEADING_TEXT = "Nitrogen Fertiliser Emissions"
export const FERTILISER_ANALYSIS_OF_TOTAL_EMISSION_HEADING_TEXT = "Fertiliser analysis % of total emissions"
export const ENERGY_ANALYSIS_OF_TOTAL_EMISSION_HEADING_TEXT = "Energy analysis % of total emissions"
export const EMISSIONS_AVERAGE_HEADING_TEXT =
  "Emissions per unit selected: Your values vs 2023 industry average"
  export const EMISSIONS_AVERAGE_SUBHEADING_TEXT = "Due to 2023 being an abnormal year for production, the per kg and per tray values below for the 2023 are based on the average Industry yields for the 2019-2022 period. The emission data here is from the Zespri Carbon Neutral Trial."
export const EMISSIONS_PROPORTION_HEADING_TEXT =
  "Proportion of total orchard emissions"
export const ANNUAL_EMISSIONS_HEADING_TEXT =
  "Your annual emissions are equivalent to the direct emissions of:"

export const EMISSION_CATEGORIES_TABLE = {
  id: "emissions-categories",
  columns: [
    {
      label: "Total kg CO2e",
      section: "results",
      subsection: "total",
      isSubtotal: true,
    },
    {
      label: "Total kg CO2e/resultBy",
      section: "results",
      subsection: "total",
    },
    {
      label: "kg CO2e/resultBy (Green)",
      section: "results",
      subsection: "greenEmissions",
      variety: 'green'
    },
    {
      label: "kg CO2e/resultBy (Gold)",
      section: "results",
      subsection: "goldEmissions",
      variety: 'gold'
    },
  ],
  rows: [
    {
      label: "Fuel",
      field: "fuelEmissions",
    },
    {
      label: "Electricity",
      field: "electricityEmissions",
    },
    {
      label: "Fertiliser manufacture",
      field: "fertiliserManufactureEmissions",
    },
    {
      label: "Soil emissions - fertiliser",
      field: "fertiliserSoilEmissions",
    },
    {
      label: "Compost manufacture",
      field: "compostManufactureEmissions",
    },
    {
      label: "Soil emissions - compost",
      field: "compostSoilEmissions",
    },
    {
      label: "Agrichemicals",
      field: "agrichemicalsEmissions",
    },
    {
      label: "Capital (pergola & netting)",
      field: "capitalEmissions",
    },
    {
      label: "Waste",
      field: "wasteRecyclingEmissions",
    },
    {
      label: "Soil emissions - pruning",
      field: "pruningSoilEmissions",
    }

  ],
  footers: [
    {
      label: "Total",
      field: "emissionsSubtotal",
    },
  ],
}

export const NITROGEN_FERTILISER_TABLE = {
  id: "nitrogen-fertliser",
    columns: [
    {
      label: "Total kg CO2e",
      section: "results",
      subsection: "total",
      isSubtotal: true,
    },
    {
      label: "Total kg CO2e/resultBy",
      section: "results",
      subsection: "total",
    },
    {
      label: "kg CO2e/resultBy (Green)",
      section: "results",
      subsection: "greenEmissions",
            variety: 'green'
    },
    {
      label: "kg CO2e/resultBy (Gold)",
      section: "results",
      subsection: "goldEmissions",
            variety: 'gold'
    },
  ],
  rows: [
    {
      label: "Manufacturing",
      field: "nitrogenManufacturingEmissions",
    },
    {
      label: "Soil emissions",
      field: "nitrogenFieldEmissions",
    },
  ],
  footers: [
    {
      label: "Total",
      field: "nitrogenSubtotal",
    },
    {
      label: "% of total emissions",
      field: "nitrogenPercentageOfEmissions",
      isPercentage: true,
    },
  ],
}


export const FERTILISER_ANALYSIS_TABLE = {
  id: "fertliser-analysis",
  style: "analysis-table",
  columns: [
    {
      label: "Category"
    },
    {
      label: "% of total orchard emissions"
    }
  ],
  rows: [
  ]
}

export const ENERGY_ANALYSIS_TABLE = {
  id: "energy-analysis",
  style: "analysis-table",
  columns: [
    {
      label: "Category"
    },
    {
      label: "% of total orchard emissions"
    }
  ],
  rows: [
  ]
}

export const INDUSTRY_AVERAGES = {
  perHa: {
    fuelEmissions: 1420,
    electricityEmissions: 20,
    fertiliserManufactureEmissions: 720,
    compostManufactureEmissions: 290,
    agrichemicalsEmissions: 280,
    capitalEmissions: 460,
    wasteRecyclingEmissions: 0,
    pruningSoilEmissions: 300,
    compostSoilEmissions: 260,
    fertiliserSoilEmissions: 560,
    emissionsSubtotal: 4310,
  },
  perKg: {
    fuelEmissions: 29,
    electricityEmissions: 0.4,
    fertiliserManufactureEmissions: 14,
    compostManufactureEmissions: 6,
    agrichemicalsEmissions: 6,
    capitalEmissions: 9,
    wasteRecyclingEmissions: 0,
    pruningSoilEmissions:6,
    compostSoilEmissions: 5,
    fertiliserSoilEmissions: 11,
    emissionsSubtotal: 87,
  },
  perTray: {
    fuelEmissions: 117,
    electricityEmissions: 2,
    fertiliserManufactureEmissions: 60,
    compostManufactureEmissions: 24,
    agrichemicalsEmissions: 23,
    capitalEmissions: 38,
    wasteRecyclingEmissions: 0,
    pruningSoilEmissions: 25,
    compostSoilEmissions: 24,
    fertiliserSoilEmissions: 46,
    emissionsSubtotal: 356,
  },
}

export const GRAPH_INFO = {
  yLabel: "kg CO2e/resultBy",
  groups: [
    {
      label: "2023 Industry average",
      type: "industryAverage",
    },
    {
      label: "Your values",
      type: "userData",
    },
  ],
  fields: [
    {
      field: "fuelEmissions",
      section: "results",
      label: "Fuel",
    },
    {
      field: "electricityEmissions",
      section: "results",
      label: "Electricity",
    },
    {
      field: "fertiliserManufactureEmissions",
      section: "results",
      label: "Fertiliser manufacture",
    },
    {
      field: "fertiliserSoilEmissions",
      section: "results",
      label: "Soil emissions - fertilisers",
    },
    {
      field: "compostManufactureEmissions",
      section: "results",
      label: "Compost manufacture",
    },
    {
      field: "compostSoilEmissions",
      section: "results",
      label: "Soil emissions - compost",
    },
    {
      field: "agrichemicalsEmissions",
      section: "results",
      label: "Agrichemicals",
    },
    {
      field: "capitalEmissions",
      section: "results",
      label: "Capital (pergola & netting)",
    },
    {
      field: "wasteRecyclingEmissions",
      section: "results",
      label: "Waste",
    },
    {
      field: "pruningSoilEmissions",
      section: "results",
      label: "Soil emissions - pruning & leaves",
    },
    {
      label: "Total",
      field: "emissionsSubtotal",
      section: "results",
    },
  ]
}

export const EMISSIONS_EQUIVALENT_VALUES_INFO = {
  kmDriven: {
    divisor: 0.265,
  },
  flights: {
    divisor: 2679.83,
  },
  pineHectares: {
    divisor: 28571,
  },
  forestHectares: {
    divisor: 6480,
  },
}

export const VIEW_NOT_AVAILABLE_HEADING_TEXT = "Page not available"
export const UNAVAILABLE_VIEWS_LIST_TEXT =
  "You have not yet completed the following pages:"
export const UNAVAILABLE_VIEWS_BODY_TEXT =
  "Please complete the previous pages to access this page."
export const GO_TO_AVAILABLE_VIEW_TEXT = "Go to ${this.title}"
export const PAGE_NOT_FOUND_HEADING_TEXT = "Page not found"
export const PAGE_NOT_FOUND_BODY_TEXT =
  "The page you were looking for does not exist. Please make sure the URL you entered is correct."
export const CLOSE_MODAL_TEXT = "Close"
export const CANCEL_MODDAL_TEXT = "Cancel"
export const ORCHARD_DATA_KEY = "orchardData"
export const INPUT_DATA_KEY = "inputData"
export const EMAIL_MODAL_HEADING_TEXT = "We've created a link to your results"
export const EMAIL_MODAL_BODY_TEXT = [
  "We can email through a link to your greenhouse gas emission results.",
  "Simply enter the email address you'd like to send the results to in the box below.",
  "Alternatively you can simply copy and paste this link to your results directly.",
]

export const EMAIL_LABEL_TEXT = "Email address"
export const EMAIL_MODAL_BUTTON_TEXT = "Email me a copy of these results"
export const OPEN_EMAIL_BUTTON_TEXT = "Email me a link to this page"
export const PRINT_BUTTON_TEXT = "Print results"
export const EMAIL_SENT_SUCCESS_TEXT = "Email has been sent to "
export const NETTING_OVERHEAD_SUBFIELDS =  ["overheadTotalLengthOfNetting", "overheadTotalWidthOfNetting", "overheadTotalNumOfPosts", "overheadLengthPerPost", "overheadDiameterPerPost", "overheadTotalSteelCableLength"];
export const NETTING_ARTIFICIAL_SUBFIELDS =  ["artificialTotalLengthOfNetting", "artificialTotalWidthOfNetting", "artificialTotalNumberOfPost", "artificialDiameterPerPost", "artificialLengthPerPost", "artificialTotalSteelCableLength"];
export const NETTING_UNDERVINE_SUBFIELDS =  ["undervineTotalLengthOfNetting", "undervineTotalWidthOfNetting", "undervineWiresTotalLength"];
export const VALIDATION_MESSAGE_REQUIRED = "Required";
export const EMAIL_SENT_SUCCESS_TITLE = "Email sent";
export const SENDING_EMAIL_TITLE = "Sending email";
export const CLONE_RESULTS_TITLE = "Clone existing results";
export const CLONE_RESULT_SUCCESS_TITLE = "Results cloned";
export const CLONE_RESULTS_BUTTON_TEXT = "Continue";
export const CLONE_RESULTS_SUCCESS_TITLE = "Continue";
export const CLONE_RESULTS_SUCCESS_TEXT = "Data has been cloned, you can now edit this form and calculate a new result.";
export const CLONE_STATUS_INITIALISE = "Clone Initialise";
export const CLONE_STATUS_CLONING = "Cloning";
export const CLONE_STATUS_SUCCESS = "Clone Success";
export const CLONE_STATUS_ERROR = "Error";
export const DELETE_STATUS_INITIALISE = "Delete Initialise"
export const DELETE_STATUS_DELETING = "Deleting";
export const DELETE_STATUS_SUCCESS = "Delete Success";
export const DELETE_STATUS_ERROR = "Delete Error";
export const DELETE_RESULTS_TITLE = "Delete calculation";
export const DELETE_RESULTS_TEXT = "Are you sure you want to delete this calculation?";
export const DELETE_RESULTS_BUTTON_TEXT = "Delete";
export const DELETE_RESULTS_SUCCESS_TITLE = "Calculation deleted";
export const CALCULATION_STATUS_DRAFT = "Draft";
export const CALCULATION_STATUS_COMPLETED = "Completed";
export const CALCULATION_SAVE_TIMER_DURATION = 5000;

export const WHAT_IF_CONSTANTS = {
  compostEmissionsFactor: 0.05,
  nitrousOxideEmissionsPotential: 273,
  dieselLifeCycle: 3.147,
  CAN:	4.22,
SOA	:3.85,
DAP:	7.11,
OTHER	:3.75,
Urea:	2.12,
  "machineryFuelConsumption": {
   "harvest": 10.3,
   "helicopter": 120,
   "mowing": 10.3,
   "mulching": 17.5,
   "other": 10.3,
   "spraying": 10.3,
   "trimming": 15
  }
}

export const WHAT_IF_VIEW = {
  description:
    "This page provides an opportunity for you to adjust inputs and yields to measure the effect this will have on estimated emissions. The information here relates to the whole orchard and is not specific to one variety.",
  sections: [
    {
      title: "Change inputs",
      inputFields: [
        {
          type: "table",
          section: "whatif",
          columns: [
            {
              subsection: "currentValues",
              label: "Current values",
              readOnly: true
            },
            {
              subsection: "newValues",
              label: "Enter new values"
            },
            {
              subsection: "changeToTotals",
              label: "Change in total orchard emissions",
              readOnly: true
            }
          ],
          rows: [
            {
              label: "Your diesel use (L)",
              type: "number",
              min: 0,
              field: "yourDieselUseInput"
            },
            {
              label: "Contractor hours - shelter belt trimming (hours)",
              type: "number",
              min: 0,
              field: "contractorShelterBeltTrimmingHoursInput"
            },
            {
              label: "Contractor hours - mulching",
              type: "number",
              min: 0,
              field: "contractorMulchingHoursInput"
            },
            {
              label: "Contractor hours - mowing, spraying, other",
              type: "number",
              min: 0,
              field: "contractorMowingSprayingOtherHoursInput"
            },
            {
              label: "Contractor hours - helicopter",
              type: "number",
              min: 0,
              field: "contractorHelicopterHoursInput"
            },
            {
              label: "Electricity use (kWh)",
              type: "number",
              min: 0,
              field: "electricityUsedInput"
            },
            {
              label:
                "Soil applied inorganic nitrogen; non-urea (average kg N/ha)",
              type: "number",
              field: "soilAppliedInorganicNitrogenNonUreaAverageInput"
            },
            {
              label: "Soil applied potassium (kg K/ha)",
              type: "number",
              field: "soilAppliedPotassiumInput"
            },
            {
              label: "Compost (t/ha)",
              type: "number",
              field: "compostAverageInput"
            }
          ]
        }
      ]
    },
    {
      title: "Change yield",
      inputFields: [
        {
          type: "table",
          section: "whatif",
          columns: [
            {
              subsection: "currentValues",
              label: "Current values",
              readOnly: true
            },
            {
              subsection: "newValues",
              label: "Enter new values"
            },
            {
              subsection: "changeToTotals",
              label: "Change in emissions per kg of fruit",
              readOnly: true
            }
          ],
          rows: [
            {
              label: "kg per hectare",
              type: "number",
              min: 0,
              field: "yourKgPerHectareInput"
            },
            {
              label: "Class 1 Trays per hectare",
              type: "number",
              min: 0,
              field: "class1TraysPerHectareInput"
            },
            
          ]
        }
      ]
    },

  ]
};


export const WHAT_IF_GRAPH_INFO = {
  yLabel: "Greenhouse gas emissions (kg CO₂ eq)",
  groups: [
    {
      label: "Current Emissions",
      type: "currentEmissions",
    },
    {
      label: "New Emissions",
      type: "newEmissions",
    },
  ],
  fields: [
   {
      label: "Total",
      field: "emissionsSubtotal",
      section: "whatIf",
    },
       {
      field: "compostAverageInput",
      section: "whatIf",
      label: "Compost",
    },
        {
      field: "soilAppliedPotassiumInput",
      section: "whatIf",
      label: "Potassium",
    },
        {
      field: "soilAppliedInorganicNitrogenNonUreaAverageInput",
      section: "whatIf",
      label: "Inorganic soil applied N (non-urea)",
    },
    {
      field: "electricityUsedInput",
      section: "whatIf",
      label: "Electricity",
    }, 
    {
      field: "contractorHelicopterHoursInput",
      section: "whatIf",
      label: "Contractor helicopter fuel use",
    },
    {
      field: "contractorMowingSprayingOtherHoursInput",
      section: "whatIf",
      label: "Contractor mowing, spraying, other",
    },
    {
      field: "contractorMulchingHoursInput",
      section: "whatIf",
      label: "Contractor mulching",
    },
    {
      field: "contractorShelterBeltTrimmingHoursInput",
      section: "whatIf",
      label: "Contractor shelter belt trimming",
    },

    {
      field: "yourDieselUseInput",
      section: "whatIf",
      label: "Diesel use",
    },
 
  ],
}

export const WHAT_IF_EMISSIONS_GRAPH_HEADING_TEXT =
  "Effect of input changes"

export const WHAT_IF_GRAPH_INFO_DEFAULT = [
    [
        {
            "x": "Total",
            "y": 0
        },
        {
            "x": "Compost",
            "y": 0
        },
        {
            "x": "Potassium",
            "y": 0
        },
        {
            "x":  "Inorganic soil applied N (non-urea)",
            "y": 0
        },
        {
            "x": "Electricity",
            "y": 0
        },
        {
            "x":"Contractor helicopter fuel use",
            "y":0
        },
        {
            "x": "Contractor mowing, spraying, other",
            "y": 0
        },
        {
            "x":  "Contractor mulching",
            "y": 0
        },
        {
            "x": "Contractor shelter belt trimming",
            "y": 0
        },
        {
            "x": "Diesel use",
            "y": 0
        },
    ],
    [
        {
            "x": "Total",
            "y": 0
        },
        {
            "x": "Compost",
            "y":0
        },
        {
            "x": "Potassium",
            "y":0
        },
        {
            "x":  "Inorganic soil applied N (non-urea)",
            "y":0
        },
        {
            "x": "Electricity",
            "y": 0
        },
        {
            "x":"Contractor helicopter fuel use",
            "y": 0
        },
        {
            "x": "Contractor mowing, spraying, other",
            "y":  0 
        },
        {
            "x":  "Contractor mulching",
            "y": 0
        },
        {
            "x": "Contractor shelter belt trimming",
            "y":0
        },
        {
            "x": "Diesel use",
            "y": 0
        },
    ],
]

export const EMISSION_FIELDS = [
  "nitrogenManufacturingEmissions",
  "fuelEmissions",
  "fertiliserSoilEmissions",
  "nitrogenPercentageOfEmissions",
  "fertiliserManufactureEmissions",
  "wasteRecyclingEmissions",
  "compostSoilEmissions",
  "nitrogenFieldEmissions",
  "capitalEmissions",
  "agrichemicalsEmissions",
  "electricityEmissions",
  "pruningSoilEmissions",
  "compostManufactureEmissions",
]

export const NON_SAVING_URLS = ["/orchard-overview", "/summary", "/results", "/whatIf", "/introduction"];
